import { roundTo } from './format';

/**
 * A small, safe expression language for calculated rows ("PCV * 10 / RBC").
 * No eval: formulas are parsed into an AST and evaluated against row codes.
 */
export type Expr =
  | { t: 'num'; v: number }
  | { t: 'ref'; code: string }
  | { t: 'neg'; e: Expr }
  | { t: 'bin'; op: '+' | '-' | '*' | '/' | '^'; a: Expr; b: Expr }
  | { t: 'call'; fn: FnName; args: Expr[] };

const FUNCTIONS = {
  min: (...xs: number[]) => Math.min(...xs),
  max: (...xs: number[]) => Math.max(...xs),
  abs: (x: number) => Math.abs(x),
  sqrt: (x: number) => Math.sqrt(x),
  log10: (x: number) => Math.log10(x),
  ln: (x: number) => Math.log(x),
  round: (x: number, d = 0) => roundTo(x, d),
} as const;
type FnName = keyof typeof FUNCTIONS;

export class FormulaSyntaxError extends Error {
  constructor(
    message: string,
    readonly position: number,
  ) {
    super(message);
    this.name = 'FormulaSyntaxError';
  }
}

type Token =
  | { k: 'num'; v: number; p: number }
  | { k: 'id'; v: string; p: number }
  | { k: 'op'; v: string; p: number }
  | { k: 'end'; p: number };

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i] as string;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    const num = /^(\d+(\.\d*)?|\.\d+)/.exec(src.slice(i));
    if (num) {
      out.push({ k: 'num', v: Number(num[0]), p: i });
      i += num[0].length;
      continue;
    }
    const id = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i));
    if (id) {
      out.push({ k: 'id', v: id[0], p: i });
      i += id[0].length;
      continue;
    }
    if ('+-*/^(),'.includes(c)) {
      out.push({ k: 'op', v: c, p: i });
      i++;
      continue;
    }
    throw new FormulaSyntaxError(`Unexpected "${c}"`, i);
  }
  out.push({ k: 'end', p: src.length });
  return out;
}

export function parseFormula(src: string): Expr {
  const toks = tokenize(src);
  let pos = 0;
  const peek = () => toks[pos] as Token;
  const next = () => toks[pos++] as Token;
  const isOp = (v: string) => {
    const t = peek();
    return t.k === 'op' && t.v === v;
  };
  const expect = (v: string) => {
    const t = next();
    if (t.k !== 'op' || t.v !== v) throw new FormulaSyntaxError(`Expected "${v}"`, t.p);
  };

  function expr(): Expr {
    let a = term();
    while (isOp('+') || isOp('-')) {
      const op = (next() as { v: '+' | '-' }).v;
      a = { t: 'bin', op, a, b: term() };
    }
    return a;
  }
  function term(): Expr {
    let a = unary();
    while (isOp('*') || isOp('/')) {
      const op = (next() as { v: '*' | '/' }).v;
      a = { t: 'bin', op, a, b: unary() };
    }
    return a;
  }
  // Unary minus binds looser than ^, so -A^2 is -(A^2) as in ordinary maths.
  function unary(): Expr {
    if (isOp('-')) {
      next();
      return { t: 'neg', e: unary() };
    }
    if (isOp('+')) {
      next();
      return unary();
    }
    return power();
  }
  function power(): Expr {
    const a = primary();
    if (isOp('^')) {
      next();
      return { t: 'bin', op: '^', a, b: unary() };
    }
    return a;
  }
  function primary(): Expr {
    const t = next();
    if (t.k === 'num') return { t: 'num', v: t.v };
    if (t.k === 'id') {
      if (isOp('(')) {
        const fn = t.v.toLowerCase();
        if (!(fn in FUNCTIONS)) throw new FormulaSyntaxError(`Unknown function ${t.v}`, t.p);
        next();
        const args: Expr[] = [];
        if (!isOp(')')) {
          args.push(expr());
          while (isOp(',')) {
            next();
            args.push(expr());
          }
        }
        expect(')');
        return { t: 'call', fn: fn as FnName, args };
      }
      return { t: 'ref', code: t.v.toUpperCase() };
    }
    if (t.k === 'op' && t.v === '(') {
      const e = expr();
      expect(')');
      return e;
    }
    throw new FormulaSyntaxError(
      t.k === 'end' ? 'Formula ends too early' : 'Unexpected token',
      t.p,
    );
  }

  const result = expr();
  if (peek().k !== 'end') throw new FormulaSyntaxError('Unexpected text after formula', peek().p);
  return result;
}

export function references(e: Expr, into = new Set<string>()): Set<string> {
  switch (e.t) {
    case 'ref':
      into.add(e.code);
      break;
    case 'neg':
      references(e.e, into);
      break;
    case 'bin':
      references(e.a, into);
      references(e.b, into);
      break;
    case 'call':
      for (const a of e.args) references(a, into);
      break;
  }
  return into;
}

export type EvalResult =
  | { ok: true; value: number }
  | { ok: false; reason: 'missing'; missing: string[] }
  | { ok: false; reason: 'math' };

export function evaluateFormula(e: Expr, env: ReadonlyMap<string, number | null>): EvalResult {
  const missing = [...references(e)].filter((c) => env.get(c) == null);
  if (missing.length) return { ok: false, reason: 'missing', missing };
  const v = run(e, env);
  return Number.isFinite(v) ? { ok: true, value: v } : { ok: false, reason: 'math' };
}

function run(e: Expr, env: ReadonlyMap<string, number | null>): number {
  switch (e.t) {
    case 'num':
      return e.v;
    case 'ref':
      return env.get(e.code) as number;
    case 'neg':
      return -run(e.e, env);
    case 'bin': {
      const a = run(e.a, env);
      const b = run(e.b, env);
      if (e.op === '+') return a + b;
      if (e.op === '-') return a - b;
      if (e.op === '*') return a * b;
      if (e.op === '/') return b === 0 ? Number.NaN : a / b;
      return a ** b;
    }
    case 'call': {
      const fn = FUNCTIONS[e.fn] as (...xs: number[]) => number;
      return fn(...e.args.map((a) => run(a, env)));
    }
  }
}
